import React, { useState } from 'react';
import { ModalHeader, Table } from 'reactstrap';
import { IAdvanceRequestType } from '../../../types/AdvanceRequestTypes';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { getAdvanceIdLabel } from '../../../types/AdvanceTypeMapping';

interface AdvanceStatusInfoTableProps {
    advanceRequest: IAdvanceRequestType;
}

const AdvanceProviderInformationTable: React.FC<AdvanceStatusInfoTableProps> = ({ advanceRequest }) => {
    const token = advanceRequest?.metadata?.data?.voucher || advanceRequest?.metadata?.data?.voucher_number || advanceRequest?.metadata?.data?.pin;

    const [isVisible, setIsVisible] = useState(false);
    return (
        <>
            <ModalHeader className="history-table-header">
                Status Information
            </ModalHeader>
            <Table borderless>
                <thead className="thead-light">
                    <tr>
                        <th>UUID</th>
                        <th>{getAdvanceIdLabel(advanceRequest.advance_type, advanceRequest.metadata?.service_provider, advanceRequest.payment_provider)} ID</th>
                        {token && <th>Token</th>}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td onClick={() => copyTextToClipboard(advanceRequest.uuid)}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none">
                                <path
                                    d="M16 8V6C16 5.46957 15.7893 4.96086 15.4142 4.58579C15.0391 4.21071 14.5304 4 14 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V14C4 14.5304 4.21071 15.0391 4.58579 15.4142C4.96086 15.7893 5.46957 16 6 16H8M8 10C8 9.46957 8.21071 8.96086 8.58579 8.58579C8.96086 8.21071 9.46957 8 10 8H18C18.5304 8 19.0391 8.21071 19.4142 8.58579C19.7893 8.96086 20 9.46957 20 10V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H10C9.46957 20 8.96086 19.7893 8.58579 19.4142C8.21071 19.0391 8 18.5304 8 18V10Z"
                                    stroke="black"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </td>
                        <td onClick={() => copyTextToClipboard(advanceRequest.payment_provider === 'paysoft' ? String(advanceRequest.id) : advanceRequest.source_id)}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none">
                                <path
                                    d="M16 8V6C16 5.46957 15.7893 4.96086 15.4142 4.58579C15.0391 4.21071 14.5304 4 14 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V14C4 14.5304 4.21071 15.0391 4.58579 15.4142C4.96086 15.7893 5.46957 16 6 16H8M8 10C8 9.46957 8.21071 8.96086 8.58579 8.58579C8.96086 8.21071 9.46957 8 10 8H18C18.5304 8 19.0391 8.21071 19.4142 8.58579C19.7893 8.96086 20 9.46957 20 10V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H10C9.46957 20 8.96086 19.7893 8.58579 19.4142C8.21071 19.0391 8 18.5304 8 18V10Z"
                                    stroke="black"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </td>
                        {token && (
                            <td>
                                {/* replace password string with ********** */}
                                <span>
                                    {isVisible ? token : '*'.repeat(10)}
                                </span>
                                <PasswordToggleIcon
                                    isVisible={isVisible}
                                    onClick={() => setIsVisible(!isVisible)}
                                    size={24}
                                    className="ml-2"
                                    strokeWidth={2}
                                    color="currentColor"
                                />
                            </td>
                        )}
                    </tr>
                </tbody>
            </Table>
        </>
    );
};

export default AdvanceProviderInformationTable;

interface PasswordToggleIconProps {
    isVisible: boolean;
    onClick: () => void;
    size?: number;
    className?: string;
    strokeWidth?: number;
    color?: string;
}
const PasswordToggleIcon: React.FC<PasswordToggleIconProps> = ({
    isVisible = false,
    onClick,
    size = 24,
    className = "",
    strokeWidth = 2,
    color = "currentColor"
}) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={onClick}
            className={`cursor-pointer ${className}`}
        >
            {isVisible ? (
                // Eye open (show password)
                <g>
                    <path
                        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
            ) : (
                // Eye closed with slash (hide password)
                <g>
                    <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M1 1l22 22"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
            )}
        </svg>
    );